import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import jsQR from "jsqr";

// Change #100098 - QR set-up for the Clock In/Out kiosk.
//
// Both halves run entirely in the browser. The QR holds a live kiosk token, so
// it is never sent to an online QR image service, and the camera frames never
// leave the tablet.
//
// `qrcode` and `jsqr` are plain JS rather than React components on purpose: the
// workspace root carries React 19 for the mobile app, and a React QR component
// hoisted there would bind to it and crash this React 18 app.

// Renders `value` as a QR code image.
export function KioskQrImage({ value, size = 260 }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (!value) {
      setSrc("");
      return undefined;
    }
    QRCode.toDataURL(value, { width: size, margin: 2, errorCorrectionLevel: "M" })
      .then((url) => !cancelled && setSrc(url))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (failed) return <div className="text-danger">Could not draw the QR code.</div>;
  if (!src) return <div style={{ width: size, height: size }} />;
  return <img src={src} width={size} height={size} alt="Kiosk setup QR code" />;
}

// Live camera viewfinder that calls onResult(text) with the first QR code it
// reads for which accept(text) is truthy. Anything else (a menu, a wifi
// sticker) is ignored and scanning carries on.
export function KioskQrScanner({ onResult, accept = () => true, className = "" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState("");
  const [cameras, setCameras] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(-1); // -1 = let the browser pick
  const [ignored, setIgnored] = useState(false);

  // Latest callbacks without restarting the camera when the parent re-renders.
  const onResultRef = useRef(onResult);
  const acceptRef = useRef(accept);
  onResultRef.current = onResult;
  acceptRef.current = accept;

  useEffect(() => {
    let stream = null;
    let frame = 0;
    let stopped = false;
    let lastIgnored = "";

    if (!navigator.mediaDevices?.getUserMedia) {
      setError(
        "This browser cannot open the camera here. Camera access needs the page to be on https."
      );
      return undefined;
    }

    const deviceId = cameraIndex >= 0 ? cameras[cameraIndex]?.deviceId : null;
    const constraints = {
      audio: false,
      video: deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: { ideal: "environment" } },
    };

    const tick = () => {
      if (stopped) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState >= 2 && video.videoWidth) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(image.data, image.width, image.height, {
          inversionAttempts: "dontInvert",
        });
        if (code?.data) {
          if (acceptRef.current(code.data)) {
            stopped = true;
            onResultRef.current(code.data);
            return;
          }
          if (code.data !== lastIgnored) {
            lastIgnored = code.data;
            setIgnored(true);
          }
        }
      }
      frame = requestAnimationFrame(tick);
    };

    setError("");
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(async (s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const video = videoRef.current;
        if (video) {
          video.srcObject = s;
          video.setAttribute("playsinline", "true"); // iOS: no fullscreen takeover
          await video.play().catch(() => {});
        }
        // Labels and the full list are only available once permission is granted.
        if (!cameras.length && navigator.mediaDevices.enumerateDevices) {
          const all = await navigator.mediaDevices.enumerateDevices();
          const videos = all.filter((d) => d.kind === "videoinput");
          if (!stopped && videos.length > 1) setCameras(videos);
        }
        frame = requestAnimationFrame(tick);
      })
      .catch((e) => {
        if (stopped) return;
        setError(
          e?.name === "NotAllowedError"
            ? "Camera access was blocked. Allow the camera for this page, or enter the token instead."
            : e?.name === "NotFoundError"
            ? "No camera was found on this device. Enter the token instead."
            : "Could not start the camera. Enter the token instead."
        );
      });

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    // cameras is only read to pick the device; switching is driven by cameraIndex.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraIndex]);

  if (error) return <div className="kiosk__error">{error}</div>;

  return (
    <div className={`kiosk__scanner ${className}`}>
      <div className="kiosk__scanner-frame">
        <video ref={videoRef} muted playsInline />
        <span className="kiosk__scanner-target" />
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      {ignored && (
        <div className="kiosk__hint kiosk__scanner-note">
          That QR code is not a kiosk setup code. Use the one from Clock-In Kiosks settings.
        </div>
      )}
      {cameras.length > 1 && (
        <div className="kiosk__actions">
          <button
            className="kiosk__btn kiosk__btn--ghost"
            onClick={() => setCameraIndex((i) => (i + 1) % cameras.length)}
          >
            Switch camera
          </button>
        </div>
      )}
    </div>
  );
}
